import { type CSSProperties, type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "../components/ui";
import type { ParentType, PedigreeDocument, PedigreeNode } from "./api";
import "../styles/pedigree-v2.css";

export type GenerationCount = 3 | 4 | 5;

const CARD_WIDTH = 248;
const COLUMN_GAP = 56;
const HEADER_HEIGHT = 42;

const dateFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeZone: "Asia/Ho_Chi_Minh",
});

function rowHeight(generations: GenerationCount): number {
  return generations === 3 ? 104 : generations === 4 ? 90 : 80;
}

function centerY(node: PedigreeNode, generations: GenerationCount): number {
  const span = 2 ** (generations - node.generation);
  return (node.slot * span + span / 2) * rowHeight(generations);
}

function sexLabel(value: "male" | "female" | "unknown"): string {
  return value === "male" ? "Đực" : value === "female" ? "Cái" : "Chưa rõ";
}

function sexSymbol(value: "male" | "female" | "unknown"): string {
  return value === "male" ? "♂" : value === "female" ? "♀" : "•";
}

function parentLabel(value: ParentType | null): string {
  return value === "sire" ? "Cha" : value === "dam" ? "Mẹ" : "Hồ sơ gốc";
}

function sourceLabel(value: string): string {
  const labels: Record<string, string> = {
    owner_statement: "Xác nhận của người quản lý",
    registry_document: "Tài liệu đăng ký",
    verified_database: "Cơ sở dữ liệu đã xác minh",
    import: "Import có kiểm soát",
    moderator: "Moderator ghi nhận",
    legacy: "Dữ liệu kế thừa",
  };
  return labels[value] ?? value;
}

function pathLabel(path: string): string {
  if (path === "R") return "Gốc";
  return path
    .slice(1)
    .split("")
    .map((part) => (part === "S" ? "Cha" : "Mẹ"))
    .join(" › ");
}

function generationTitle(generation: number): string {
  if (generation === 0) return "Hồ sơ";
  return `Đời ${generation}`;
}

function Connectors({ pedigree }: { pedigree: PedigreeDocument }) {
  const width =
    (pedigree.generations + 1) * CARD_WIDTH + pedigree.generations * COLUMN_GAP;
  const height = 2 ** pedigree.generations * rowHeight(pedigree.generations);
  const byPath = new Map(pedigree.nodes.map((node) => [node.path, node]));
  const paths: ReactNode[] = [];

  for (const node of pedigree.nodes) {
    if (node.generation >= pedigree.generations) continue;
    const startX =
      node.generation * (CARD_WIDTH + COLUMN_GAP) + CARD_WIDTH;
    const endX =
      (node.generation + 1) * (CARD_WIDTH + COLUMN_GAP);
    const elbowX = startX + COLUMN_GAP / 2;
    const startY = centerY(node, pedigree.generations);

    for (const suffix of ["S", "D"] as const) {
      const ancestor = byPath.get(`${node.path}${suffix}`);
      if (!ancestor) continue;
      const endY = centerY(ancestor, pedigree.generations);
      paths.push(
        <path
          key={`${node.occurrenceId}-${suffix}`}
          d={`M ${startX} ${startY} H ${elbowX} V ${endY} H ${endX}`}
        />,
      );
    }
  }

  return (
    <svg
      className="pedigree-connectors"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      aria-hidden="true"
      focusable="false"
    >
      {paths}
    </svg>
  );
}

function PedigreeCard({
  node,
  onQuickView,
  onAdd,
  canAdd,
}: {
  node: PedigreeNode;
  onQuickView: (node: PedigreeNode) => void;
  onAdd?: (type: ParentType) => void;
  canAdd: boolean;
}) {
  const compact = node.generation >= 3;
  if (!node.dog) {
    return (
      <article
        className="pedigree-card pedigree-card--missing"
        data-generation={node.generation}
      >
        <p className="pedigree-card__relation">{pathLabel(node.path)}</p>
        <strong>Chưa có {parentLabel(node.parentType).toLowerCase()}</strong>
        {!compact ? <span>Chưa có dữ liệu đã được duyệt.</span> : null}
        {canAdd && node.parentType && onAdd ? (
          <Button size="sm" variant="secondary" onClick={() => onAdd(node.parentType ?? "sire")}>
            Bổ sung tổ tiên
          </Button>
        ) : null}
      </article>
    );
  }

  const verified = node.relationship
    ? node.relationship.verificationStatus === "verified"
    : node.dog.verified;
  const details = [node.dog.breedName, node.dog.colorName]
    .filter(Boolean)
    .join(" · ");

  return (
    <article
      className="pedigree-card"
      data-verified={verified ? "true" : "false"}
      data-generation={node.generation}
      data-sex={node.dog.sex}
    >
      <p className="pedigree-card__relation">{pathLabel(node.path)}</p>
      <div className="pedigree-card__identity">
        {node.dog.thumbnailUrl ? (
          <img
            src={node.dog.thumbnailUrl}
            alt={`Ảnh ${node.dog.registeredName}`}
            loading="lazy"
            width="68"
            height="60"
          />
        ) : (
          <span className="pedigree-card__photo-placeholder" aria-hidden="true">
            {sexSymbol(node.dog.sex)}
          </span>
        )}
        <div>
          <Link to={`/dogs/${node.dog.slug}`}>{node.dog.registeredName}</Link>
          <span className="pedigree-card__sex-year">
            <b aria-hidden="true">{sexSymbol(node.dog.sex)}</b> {sexLabel(node.dog.sex)} · {node.dog.birthYear ?? "Chưa rõ năm"}
          </span>
          {!compact ? <span>{details || "Chưa rõ giống / màu"}</span> : null}
        </div>
      </div>
      {!compact && node.dog.titles.length > 0 ? (
        <strong className="pedigree-card__titles">
          {node.dog.titles.slice(0, 3).map((title) => title.code).join(" · ")}
        </strong>
      ) : null}
      <div className="pedigree-card__footer">
        <Badge tone={verified ? "success" : "neutral"}>
          {verified ? "Đã xác minh" : "Chờ xác minh"}
        </Badge>
        <Button size="sm" variant="ghost" onClick={() => onQuickView(node)}>
          Xem nhanh nguồn
        </Button>
      </div>
    </article>
  );
}

function RelatedSection({ pedigree }: { pedigree: PedigreeDocument }) {
  return (
    <section className="pedigree-related" aria-label="Quan hệ liên quan">
      <article>
        <h3>Anh/chị/em</h3>
        {pedigree.siblings.length === 0 ? (
          <p>Chưa có dữ liệu siblings.</p>
        ) : (
          <ul>
            {pedigree.siblings.map((dog) => (
              <li key={dog.id}>
                <Link to={`/dogs/${dog.slug}`}>{dog.registeredName}</Link>
                <span>{dog.relation === "full" ? "Cùng cha mẹ" : "Cùng cha hoặc mẹ"}</span>
              </li>
            ))}
          </ul>
        )}
      </article>
      <article>
        <h3>Con</h3>
        {pedigree.offspring.length === 0 ? (
          <p>Chưa có dữ liệu offspring.</p>
        ) : (
          <ul>
            {pedigree.offspring.map((dog) => (
              <li key={dog.id}>
                <Link to={`/dogs/${dog.slug}`}>{dog.registeredName}</Link>
                <span>{sexLabel(dog.sex)} · {dog.birthYear ?? "Chưa rõ năm"}</span>
              </li>
            ))}
          </ul>
        )}
      </article>
      <article id="litter-links">
        <h3>Phối giống / litter</h3>
        {pedigree.litters.length === 0 ? (
          <p>Chưa có litter liên kết.</p>
        ) : (
          <ul>
            {pedigree.litters.map((litter) => (
              <li key={litter.id} id={`litter-${litter.id}`}>
                <a href={`#litter-${litter.id}`}>{litter.name}</a>
                <span>
                  {litter.bornAt
                    ? dateFormatter.format(new Date(`${litter.bornAt}T00:00:00Z`))
                    : litter.status}
                  {litter.mate ? (
                    <> · mate <Link to={`/dogs/${litter.mate.slug}`}>{litter.mate.registeredName}</Link></>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </article>
    </section>
  );
}

export function PedigreeExplorer({
  pedigree,
  onGenerationChange,
  canAdd = false,
  onAdd,
}: {
  pedigree: PedigreeDocument;
  onGenerationChange: (value: GenerationCount) => void;
  canAdd?: boolean;
  onAdd?: (type: ParentType) => void;
}) {
  const [mode, setMode] = useState<"tree" | "table">("tree");
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState<PedigreeNode | null>(null);
  const columns = useMemo(
    () =>
      Array.from({ length: pedigree.generations + 1 }, (_, generation) => ({
        generation,
        nodes: pedigree.nodes
          .filter((node) => node.generation === generation)
          .sort((left, right) => left.slot - right.slot),
      })),
    [pedigree],
  );
  const leafCount = 2 ** pedigree.generations;
  const rowsHeight = leafCount * rowHeight(pedigree.generations);
  const treeWidth =
    (pedigree.generations + 1) * CARD_WIDTH + pedigree.generations * COLUMN_GAP;
  const treeHeight = HEADER_HEIGHT + rowsHeight;

  return (
    <>
      <section className="pedigree-toolbar" aria-label="Điều khiển phả hệ">
        <div className="pedigree-toolbar__group">
          <span>Số thế hệ</span>
          {[3, 4, 5].map((value) => (
            <Button
              key={value}
              size="sm"
              variant={pedigree.generations === value ? "primary" : "secondary"}
              aria-pressed={pedigree.generations === value}
              onClick={() => onGenerationChange(value as GenerationCount)}
            >
              {value}
            </Button>
          ))}
        </div>
        <div className="pedigree-toolbar__group">
          <Button
            size="sm"
            variant={mode === "tree" ? "primary" : "secondary"}
            onClick={() => setMode("tree")}
          >
            Cây ngang
          </Button>
          <Button
            size="sm"
            variant={mode === "table" ? "primary" : "secondary"}
            onClick={() => setMode("table")}
          >
            Bảng accessible
          </Button>
        </div>
        {mode === "tree" ? (
          <div className="pedigree-toolbar__group">
            <Button
              size="sm"
              variant="secondary"
              disabled={zoom <= 0.7}
              onClick={() => setZoom((value) => Math.max(0.7, value - 0.1))}
            >
              Thu nhỏ
            </Button>
            <span>{Math.round(zoom * 100)}%</span>
            <Button
              size="sm"
              variant="secondary"
              disabled={zoom >= 1.3}
              onClick={() => setZoom((value) => Math.min(1.3, value + 0.1))}
            >
              Phóng to
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setZoom(1)}>
              Đặt lại
            </Button>
          </div>
        ) : null}
        <Button size="sm" variant="secondary" onClick={() => window.print()}>
          In / lưu PDF
        </Button>
      </section>

      {mode === "tree" ? (
        <section
          className="pedigree-tree-region"
          aria-label={`Phả hệ ${pedigree.generations} thế hệ`}
        >
          <p className="pedigree-scroll-hint">
            <span>Trên màn hình nhỏ, vuốt ngang để xem các thế hệ.</span> Kéo trong khung để theo dõi từng nhánh cha và mẹ.
          </p>
          <section
            className="pedigree-tree-scroll"
            aria-label="Cây phả hệ cuộn ngang"
            tabIndex={0}
          >
            <div
              className="pedigree-tree"
              data-generations={pedigree.generations}
              style={
                {
                  "--pedigree-scale": zoom,
                  "--pedigree-row-height": `${rowHeight(pedigree.generations)}px`,
                  "--pedigree-card-width": `${CARD_WIDTH}px`,
                  "--pedigree-column-gap": `${COLUMN_GAP}px`,
                  "--pedigree-header-height": `${HEADER_HEIGHT}px`,
                  width: treeWidth,
                  height: treeHeight,
                } as CSSProperties
              }
            >
              <Connectors pedigree={pedigree} />
              {columns.map((column) => (
                <section
                  className="pedigree-generation"
                  key={column.generation}
                  style={{ gridColumn: column.generation + 1, gridRow: "1 / -1" }}
                >
                  <h3>{generationTitle(column.generation)}</h3>
                  <div className="pedigree-generation__nodes">
                    {column.nodes.map((node) => {
                      const span = 2 ** (pedigree.generations - node.generation);
                      return (
                        <div
                          className="pedigree-node-slot"
                          key={node.occurrenceId}
                          style={{ gridRow: `${node.slot * span + 1} / span ${span}` }}
                        >
                          <PedigreeCard
                            node={node}
                            onQuickView={setSelected}
                            onAdd={onAdd}
                            canAdd={canAdd && node.generation === 1}
                          />
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </section>
        </section>
      ) : (
        <div className="pedigree-table-wrap">
          <table className="pedigree-table">
            <caption>Danh sách phả hệ thay thế cho screen reader và màn hình hẹp</caption>
            <thead>
              <tr>
                <th scope="col">Vị trí</th>
                <th scope="col">Tên</th>
                <th scope="col">Giới tính</th>
                <th scope="col">Giống / màu</th>
                <th scope="col">Năm sinh</th>
                <th scope="col">Xác minh</th>
              </tr>
            </thead>
            <tbody>
              {pedigree.nodes.map((node) => (
                <tr key={node.occurrenceId}>
                  <th scope="row">{pathLabel(node.path)}</th>
                  <td>
                    {node.dog ? (
                      <Link to={`/dogs/${node.dog.slug}`}>{node.dog.registeredName}</Link>
                    ) : (
                      "Chưa có dữ liệu"
                    )}
                  </td>
                  <td>{node.dog ? sexLabel(node.dog.sex) : "—"}</td>
                  <td>
                    {node.dog
                      ? [node.dog.breedName, node.dog.colorName].filter(Boolean).join(" · ") || "Chưa rõ"
                      : "—"}
                  </td>
                  <td>{node.dog?.birthYear ?? "—"}</td>
                  <td>
                    {node.dog &&
                    (node.relationship?.verificationStatus === "verified" ||
                      (!node.relationship && node.dog.verified))
                      ? "Đã xác minh"
                      : "Chưa xác minh"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected?.dog ? (
        <section className="pedigree-quick-view" aria-live="polite">
          <div>
            <p className="eyebrow">Quick view · {pathLabel(selected.path)}</p>
            <h3>{selected.dog.registeredName}</h3>
          </div>
          {selected.relationship ? (
            <dl>
              <div>
                <dt>Trạng thái</dt>
                <dd>
                  {selected.relationship.verificationStatus === "verified"
                    ? "Đã xác minh"
                    : "Chưa xác minh"}
                </dd>
              </div>
              <div>
                <dt>Phiên bản</dt>
                <dd>{selected.relationship.version}</dd>
              </div>
              <div>
                <dt>Nguồn</dt>
                <dd>{sourceLabel(selected.relationship.sourceType)}</dd>
              </div>
              <div>
                <dt>Mô tả</dt>
                <dd>{selected.relationship.sourceSummary}</dd>
              </div>
              <div>
                <dt>Reviewer</dt>
                <dd>{selected.relationship.reviewedBy?.displayName ?? "Chưa có"}</dd>
              </div>
            </dl>
          ) : (
            <p>Hồ sơ gốc; trạng thái xác minh lấy từ hồ sơ chó.</p>
          )}
          <div className="button-row">
            <Link
              className="ui-button ui-button--primary ui-button--sm"
              to={`/dogs/${selected.dog.slug}`}
            >
              Mở hồ sơ
            </Link>
            {selected.relationship?.sourceUrl ? (
              <a
                className="ui-button ui-button--secondary ui-button--sm"
                href={selected.relationship.sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                Mở nguồn
              </a>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>
              Đóng quick view
            </Button>
          </div>
        </section>
      ) : null}

      <RelatedSection pedigree={pedigree} />
      <p className="pedigree-performance">
        API dùng {pedigree.queryPlan.statementCount} statement cố định · trả {pedigree.queryPlan.returnedOccurrenceCount}/{pedigree.queryPlan.maxOccurrenceCount} occurrence · {pedigree.queryPlan.elapsedMs} ms.
      </p>
    </>
  );
}
